export type Stance = 'BULL' | 'BEAR' | 'CRAB';
export type HeroId =
  | 'cuntmander'
  | 'dickachu'
  | 'blowmesaur'
  | 'blowt'
  | 'pornbon'
  | 'gecqum';

export type ZoneId = 'exchange' | 'raid' | 'vault' | 'arcade';
export type RoundStatus = 'commit' | 'locked' | 'resolved' | 'void';

export type HeroDefinition = {
  id: HeroId;
  name: string;
  type: 'burn' | 'zap' | 'mold' | 'weep';
  color: string;
  accent: string;
  description: string;
  ability: string;
  abilityText: string;
  signatureMove: string;
  unlock: string;
  cardImage: string;
  body: 'dino' | 'rodent' | 'blob' | 'bomb' | 'lizard';
};

export type MarketAsset = {
  id: number;
  symbol: string;
  name: string;
  token: `0x${string}`;
  flatBandBps: number;
  sector: string;
};

export type MarketRound = {
  id: number;
  dayId: number;
  commitOpen: number;
  lockAt: number;
  resolveAt: number;
  status: RoundStatus;
  assets: MarketAsset[];
};

export type Forecast = {
  player: `0x${string}`;
  dayId: number;
  roundId: number;
  assetId: number;
  heroId: HeroId;
  stance: Stance;
  nonce: number;
  deadline: number;
};

export const ROBINHOOD_CHAIN = {
  id: 4663,
  name: 'Robinhood Chain',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc.mainnet.chain.robinhood.com'] }
  },
  blockExplorers: {
    default: {
      name: 'Robinhood Chain Blockscout',
      url: 'https://robinhoodchain.blockscout.com'
    }
  }
} as const;

export const HEROES: HeroDefinition[] = [
  {
    id: 'cuntmander',
    name: 'CUNTMANDER',
    type: 'burn',
    color: '#ff6b1a',
    accent: '#fff236',
    body: 'dino',
    description: 'Abuse dino powered by rage, beans and bad decisions.',
    ability: 'RAGE CANDLE',
    abilityText: '+50 damage when a correct directional call moves at least 1%.',
    signatureMove: 'BEANS FART FLAME',
    unlock: 'Starter',
    cardImage: '/assets/fukedex/cuntmander.webp'
  },
  {
    id: 'dickachu',
    name: 'DICKACHU',
    type: 'zap',
    color: '#ffe500',
    accent: '#21e6ff',
    body: 'rodent',
    description: 'Public Wi-Fi rodent. Zero shame. Maximum voltage.',
    ability: 'VOLTAGE TROLL',
    abilityText: '+30 damage on correct medium-volatility calls.',
    signatureMove: 'PUBLIC WI-FI ZAP',
    unlock: 'Starter',
    cardImage: '/assets/fukedex/dickachu.webp'
  },
  {
    id: 'blowmesaur',
    name: 'BLOWMESAUR',
    type: 'mold',
    color: '#6fdf4d',
    accent: '#f7d44a',
    body: 'dino',
    description: 'Permanently on break. Sometimes forgets the market exists.',
    ability: 'BRAIN FOG',
    abilityText: 'Near misses deal 60 damage instead of 40.',
    signatureMove: 'MUNCHIE MELTDOWN',
    unlock: 'Starter',
    cardImage: '/assets/fukedex/blowmessaur.webp'
  },
  {
    id: 'blowt',
    name: 'BLOWT',
    type: 'mold',
    color: '#8ae24f',
    accent: '#db63ff',
    body: 'blob',
    description: 'A suspicious snack with terminal couch energy.',
    ability: 'MIDNIGHT MUNCHIES',
    abilityText: '+35 damage on a correct Crab call.',
    signatureMove: 'ILLEGAL SNACK',
    unlock: 'Resolve 3 forecasts',
    cardImage: '/assets/fukedex/blowt.webp'
  },
  {
    id: 'pornbon',
    name: 'PORNBON',
    type: 'burn',
    color: '#ff3b66',
    accent: '#ffd84d',
    body: 'bomb',
    description: 'A living age restriction with no indoor voice.',
    ability: 'FLASH BANG',
    abilityText: '+25 damage on any correct call; wrong calls break streak XP.',
    signatureMove: 'PORN EXPLOSION',
    unlock: 'Complete 5 archive quests',
    cardImage: '/assets/fukedex/pornbon.webp'
  },
  {
    id: 'gecqum',
    name: 'GECQUM',
    type: 'weep',
    color: '#32d8d2',
    accent: '#d2ff4d',
    body: 'lizard',
    description: 'Slime lizard. Leaves charts and floors dangerously wet.',
    ability: 'SLICK FLOOR',
    abilityText: '+35 damage on a correct Bear call.',
    signatureMove: 'SPLASH PANIC',
    unlock: 'Contribute to a weekly raid',
    cardImage: '/assets/fukedex/gecqum.webp'
  }
];

export const MARKET_ASSETS: MarketAsset[] = [
  {
    id: 1,
    symbol: 'SPY',
    name: 'S&P 500 ETF',
    token: '0x117cc2133c37B721F49dE2A7a74833232B3B4C0C',
    flatBandBps: 15,
    sector: 'INDEX'
  },
  {
    id: 2,
    symbol: 'QQQ',
    name: 'Nasdaq 100 ETF',
    token: '0xD5f3879160bc7c32ebb4dC785F8a4F505888de68',
    flatBandBps: 15,
    sector: 'INDEX'
  },
  {
    id: 3,
    symbol: 'NVDA',
    name: 'NVIDIA',
    token: '0xd0601CE157Db5bdC3162BbaC2a2C8aF5320D9EEC',
    flatBandBps: 30,
    sector: 'CHIPS'
  },
  {
    id: 4,
    symbol: 'TSLA',
    name: 'Tesla',
    token: '0x322F0929c4625eD5bAd873c95208D54E1c003b2d',
    flatBandBps: 30,
    sector: 'CHAOS'
  },
  {
    id: 5,
    symbol: 'GME',
    name: 'GameStop',
    token: '0x1b0E319c6A659F002271B69dB8A7df2F911c153E',
    flatBandBps: 30,
    sector: 'MEME'
  },
  {
    id: 6,
    symbol: 'COIN',
    name: 'Coinbase',
    token: '0x6330D8C3178a418788dF01a47479c0ce7CCF450b',
    flatBandBps: 30,
    sector: 'CRYPTO'
  }
];

export function getCurrentRound(now = Date.now()): MarketRound {
  const seconds = Math.floor(now / 1000);
  const dayStart = Math.floor(seconds / 86_400) * 86_400;
  const dayId = Math.floor(seconds / 86_400);
  const slot = Math.min(2, Math.floor((seconds - dayStart) / 28_800));
  const commitOpen = dayStart + slot * 28_800;
  const lockAt = commitOpen + 21_600;
  const resolveAt = commitOpen + 28_800;
  const status: RoundStatus =
    seconds < lockAt ? 'commit' : seconds < resolveAt ? 'locked' : 'resolved';
  const offset = (dayId + slot) % MARKET_ASSETS.length;
  const assets = [0, 1, 2].map((index) => MARKET_ASSETS[(offset + index) % MARKET_ASSETS.length]);
  return { id: dayId * 3 + slot, dayId, commitOpen, lockAt, resolveAt, status, assets };
}

export function formatCountdown(targetSeconds: number, now = Date.now()): string {
  const remaining = Math.max(0, targetSeconds - Math.floor(now / 1000));
  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}
